/**
 * Live interview wire protocol between the two field devices and the gateway.
 *
 * Control messages are JSON text frames. Audio travels as binary frames:
 *
 *   Microphone frame (device -> server), 16-byte header + PCM16LE mono 16 kHz:
 *     0..1   magic "A1"
 *     2      flags: bit0 = late (sent after reconnect, recording only)
 *                   bit1 = playback (device was playing translated audio)
 *     3      reserved
 *     4..7   uint32 sequence number (per device, per session, starts at 1)
 *     8..15  float64 capture time, ms since the session clock origin
 *
 *   Voice frame (server -> interviewee device):
 *     0..1   magic "T1"
 *     2..3   uint16 length N of the turn id
 *     4..    N bytes ASCII turn id, then PCM16LE mono at TTS_SAMPLE_RATE
 */

export const MIC_SAMPLE_RATE = 16_000;
export const TTS_SAMPLE_RATE = 24_000;
export const MIC_HEADER_BYTES = 16;
export const MIC_FLAG_LATE = 0b01;
export const MIC_FLAG_PLAYBACK = 0b10;

export type DeviceRole = "interviewer" | "interviewee";
export type Speaker = DeviceRole;

export type SessionState = "pairing" | "consent" | "ready" | "live" | "paused" | "ended";

export interface LiveTurn {
  id: string;
  seq: number;
  speaker: Speaker;
  sourceLang: string;
  sourceText: string;
  targetLang: string;
  targetText: string | null;
  preparedQuestionId: string | null;
  startedMs: number;
  endedMs: number;
  origin: "live" | "recovered" | "transcribed";
  /** Record mode: "Participant 2" or a name a reviewer gave; null with two phones. */
  speakerLabel?: string | null;
}

export interface TurnLatency {
  /** Speech ended (recogniser end-of-speech event), session clock ms. */
  speechEndMs?: number;
  /** Final transcript available. */
  finalMs?: number;
  /** Translation available. */
  translatedMs?: number;
  /** First synthesised audio byte sent. */
  firstAudioMs?: number;
  /** Interviewee device reported playback start. */
  playbackMs?: number;
  /** Total: speech end -> playback (or -> translated text when no voice). */
  totalMs?: number;
  prepared?: boolean;
}

export interface TopicItem {
  questionId: string;
  topic: string;
  text: string;
  /** Verified translation in the interviewee's language, if any. */
  translation: string | null;
  verified: boolean;
  probes: string[];
}

export interface SessionSummary {
  interviewId: string;
  engagementName: string;
  /** Who the consent names as responsible for the data, and the country whose rules apply. */
  controller: string;
  country: string;
  /** Languages the interviewee may choose from (engagement setting). */
  intervieweeLanguages: string[];
  interviewerLang: string;
  intervieweeLang: string;
  translation: boolean;
  /** "record": one phone records the room; no pairing, no voice. */
  mode: "two_phone" | "record";
  /** How many people besides the consultant (record mode). */
  participants: number;
  state: SessionState;
  consent: { accepted: boolean; at: string | null } | null;
  /** Remaining recording seconds when a plan limit applies, else null. */
  limitSeconds: number | null;
  clockOrigin: number;
}

// ---------- device -> server ----------

export type ClientMessage =
  /**
   * Interviewer: `token` is the signed-in user's ID token, `tenant` the client
   * workspace slug, `interviewId` the interview. Interviewee: `token` is the
   * one-time pairing code from the QR; the server resolves the rest.
   */
  | {
      t: "hello";
      role: DeviceRole;
      token: string;
      deviceId: string;
      tenant?: string;
      interviewId?: string;
      /** Device clock minus server clock is learned from ping/pong; sent for diagnostics. */
      client?: { ua: string; tz: string };
    }
  | { t: "consent"; accepted: boolean; lang: string; textVersion: string }
  | { t: "start" }
  | { t: "pause" }
  | { t: "resume" }
  | { t: "end" }
  | { t: "playback"; state: "start" | "end"; turnId: string; atMs: number }
  | { t: "topic"; questionId: string; covered: boolean }
  | { t: "lang"; lang: string }
  | { t: "speak-answers"; enabled: boolean }
  | { t: "ping"; atMs: number };

// ---------- server -> device ----------

export type ServerMessage =
  | {
      t: "welcome";
      role: DeviceRole;
      session: SessionSummary;
      lastAckSeq: number;
      turns: LiveTurn[];
      /** Interviewer only: the guide, as topics to cover rather than a script. */
      topics: TopicItem[];
      peers: { interviewer: boolean; interviewee: boolean };
      coveredQuestionIds: string[];
    }
  | { t: "peer"; role: DeviceRole; connected: boolean }
  | {
      t: "state";
      state: SessionState;
      consent?: SessionSummary["consent"];
      /** Session clock origin (server epoch ms) once the interview has started. */
      clockOrigin?: number;
      /** Present when the interviewee changed language before consent. */
      intervieweeLang?: string;
    }
  | { t: "caption"; speaker: Speaker; lang: string; text: string }
  | { t: "turn"; turn: LiveTurn }
  | { t: "translation"; turnId: string; lang: string; text: string; final: boolean }
  /** Translated voice for a turn follows as binary voice frames. `prepared` = verified pre-generated audio. */
  | { t: "voice-start"; turnId: string; sampleRate: number; prepared: boolean }
  | { t: "voice-end"; turnId: string }
  | { t: "ack"; seq: number }
  | { t: "covered"; questionId: string; covered: boolean }
  | { t: "suggestion"; turnId: string; kind: "follow_up" | "missing_number" | "risk"; text: string }
  | { t: "latency"; turnId: string; latency: TurnLatency }
  | { t: "limit"; remainingSeconds: number; reached: boolean }
  | { t: "pong"; atMs: number; serverMs: number }
  | { t: "error"; code: string; message: string };

// ---------- binary helpers (shared by browser and server) ----------

export function encodeMicFrame(
  seq: number,
  captureMs: number,
  flags: number,
  pcm: Int16Array,
): ArrayBuffer {
  const buf = new ArrayBuffer(MIC_HEADER_BYTES + pcm.byteLength);
  const view = new DataView(buf);
  view.setUint8(0, 0x41); // A
  view.setUint8(1, 0x31); // 1
  view.setUint8(2, flags);
  view.setUint8(3, 0);
  view.setUint32(4, seq, true);
  view.setFloat64(8, captureMs, true);
  new Int16Array(buf, MIC_HEADER_BYTES).set(pcm);
  return buf;
}

export interface MicFrame {
  seq: number;
  captureMs: number;
  late: boolean;
  playback: boolean;
  pcm: Uint8Array;
}

export function decodeMicFrame(data: Uint8Array): MicFrame | null {
  if (data.byteLength < MIC_HEADER_BYTES || data[0] !== 0x41 || data[1] !== 0x31) return null;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const flags = view.getUint8(2);
  return {
    seq: view.getUint32(4, true),
    captureMs: view.getFloat64(8, true),
    late: (flags & MIC_FLAG_LATE) !== 0,
    playback: (flags & MIC_FLAG_PLAYBACK) !== 0,
    pcm: data.subarray(MIC_HEADER_BYTES),
  };
}

export function encodeVoiceFrame(turnId: string, pcm: Uint8Array): Uint8Array {
  const id = new TextEncoder().encode(turnId);
  const out = new Uint8Array(4 + id.byteLength + pcm.byteLength);
  out[0] = 0x54; // T
  out[1] = 0x31; // 1
  new DataView(out.buffer).setUint16(2, id.byteLength, true);
  out.set(id, 4);
  out.set(pcm, 4 + id.byteLength);
  return out;
}

export function decodeVoiceFrame(data: ArrayBuffer): { turnId: string; pcm: Int16Array } | null {
  const bytes = new Uint8Array(data);
  if (bytes.byteLength < 4 || bytes[0] !== 0x54 || bytes[1] !== 0x31) return null;
  const len = new DataView(data).getUint16(2, true);
  const turnId = new TextDecoder().decode(bytes.subarray(4, 4 + len));
  const start = 4 + len;
  const usable = (bytes.byteLength - start) & ~1;
  const copy = bytes.slice(start, start + usable);
  return { turnId, pcm: new Int16Array(copy.buffer) };
}
